import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@/hooks/useApiClient';
import { useToast } from '@/contexts/ToastContext';
import { useAuthStore } from '@/store/auth.store';
import { extractErrorMessage } from '@/utils/extractErrorMessage';
import { useTranslation } from 'react-i18next';

interface TeamMember {
  id: string;
  email: string;
  role: 'STAFF' | 'AUDITOR';
  displayName?: string;
}

export default function TeamMembersTab() {
  const apiClient = useApiClient();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const { t } = useTranslation();

  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [newMemberRole, setNewMemberRole] = useState<'STAFF' | 'AUDITOR'>('STAFF');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingRole, setEditingRole] = useState<'STAFF' | 'AUDITOR'>('STAFF');
  const [resetPasswordId, setResetPasswordId] = useState<string | null>(null);
  const [resetPasswordValue, setResetPasswordValue] = useState('');

  const { data: teamMembers = [], isLoading } = useQuery({
    queryKey: ['team-members', user?.establishmentId],
    queryFn: () => apiClient.getTeamMembers(user?.establishmentId || ''),
    enabled: !!user?.establishmentId && user?.role === 'OWNER',
  });

  const addMemberMutation = useMutation({
    mutationFn: async () => {
      return apiClient.addTeamMember(user?.establishmentId || '', {
        email: newMemberEmail,
        role: newMemberRole,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members', user?.establishmentId] });
      setNewMemberEmail('');
      setNewMemberRole('STAFF');
      addToast(t('settings.team.memberAdded'), 'success');
    },
    onError: (error: any) => {
      addToast(extractErrorMessage(error, t('settings.team.memberAddError')), 'error');
    },
  });

  const updateMemberMutation = useMutation({
    mutationFn: async (memberId: string) => {
      return apiClient.updateTeamMember(user?.establishmentId || '', memberId, {
        role: editingRole,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members', user?.establishmentId] });
      setEditingId(null);
      addToast(t('settings.team.memberUpdated'), 'success');
    },
    onError: (error: any) => {
      addToast(extractErrorMessage(error, t('settings.team.memberUpdateError')), 'error');
    },
  });

  const deleteMemberMutation = useMutation({
    mutationFn: async (memberId: string) => {
      return apiClient.removeTeamMember(user?.establishmentId || '', memberId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members', user?.establishmentId] });
      addToast(t('settings.team.memberDeleted'), 'success');
    },
    onError: (error: any) => {
      addToast(extractErrorMessage(error, t('settings.team.memberDeleteError')), 'error');
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: async ({ memberId, newPassword }: { memberId: string; newPassword: string }) => {
      return apiClient.resetTeamMemberPassword(user?.establishmentId || '', memberId, newPassword);
    },
    onSuccess: () => {
      setResetPasswordId(null);
      setResetPasswordValue('');
      addToast(t('settings.team.passwordReset'), 'success');
    },
    onError: (error: any) => {
      addToast(extractErrorMessage(error, t('settings.team.passwordResetError')), 'error');
    },
  });

  if (!user || user.role !== 'OWNER') {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-900">{t('settings.team.ownerOnly')}</p>
      </div>
    );
  }

  const staffCount = teamMembers.filter((m: TeamMember) => m.role === 'STAFF').length;
  const canAddMoreStaff = staffCount < 3;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-neutral-200 p-6">
        <h2 className="text-lg font-bold text-neutral-900 mb-2">{t('settings.team.title')}</h2>
        <p className="text-sm text-neutral-600">{t('settings.team.subtitle')}</p>
      </div>

      <div className="bg-white rounded-lg border border-neutral-200 p-6">
        <h3 className="text-md font-bold text-neutral-900 mb-4">{t('settings.team.addTitle')}</h3>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-neutral-900">{t('settings.team.emailLabel')}</label>
              <input
                type="email"
                value={newMemberEmail}
                onChange={(e) => setNewMemberEmail(e.target.value)}
                placeholder={t('settings.team.emailPlaceholder')}
                className="mt-1 w-full px-4 py-2 rounded-lg border border-neutral-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-neutral-500 mt-1">{t('settings.team.emailHelper')}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-900">{t('settings.team.roleLabel')}</label>
              <select
                value={newMemberRole}
                onChange={(e) => setNewMemberRole(e.target.value as 'STAFF' | 'AUDITOR')}
                disabled={!canAddMoreStaff && newMemberRole === 'STAFF'}
                className="mt-1 w-full px-4 py-2 rounded-lg border border-neutral-200 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                <option value="STAFF" disabled={!canAddMoreStaff}>
                  {canAddMoreStaff ? t('settings.team.staffRole') : t('settings.team.staffRoleFull', { count: staffCount })}
                </option>
                <option value="AUDITOR">{t('settings.team.auditorRole')}</option>
              </select>
              <p className="text-xs text-neutral-500 mt-1">{t('settings.team.roleHelper')}</p>
            </div>
          </div>

          <button
            onClick={() => addMemberMutation.mutate()}
            disabled={!newMemberEmail || addMemberMutation.isPending || (!canAddMoreStaff && newMemberRole === 'STAFF')}
            className="px-6 py-2 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {addMemberMutation.isPending ? t('settings.team.adding') : t('settings.team.add')}
          </button>

          {!canAddMoreStaff && newMemberRole === 'STAFF' && (
            <p className="text-sm text-yellow-700 bg-yellow-50 border border-yellow-200 rounded p-3">
              {t('settings.team.staffLimitWarning')}
            </p>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg border border-neutral-200 p-6">
        <h3 className="text-md font-bold text-neutral-900 mb-4">
          {t('settings.team.currentMembers', { count: teamMembers.length })}
        </h3>

        {isLoading ? (
          <p className="text-neutral-600">{t('settings.team.loading')}</p>
        ) : teamMembers.length === 0 ? (
          <p className="text-neutral-600 text-sm">{t('settings.team.noMembers')}</p>
        ) : (
          <div className="space-y-3">
            {teamMembers.map((member: TeamMember) => (
              <div key={member.id}>
              <div className="flex items-center justify-between p-4 rounded-lg border border-neutral-200 hover:border-neutral-300 transition-colors">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-neutral-200 flex items-center justify-center text-sm font-medium">
                      {member.email.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-neutral-900">{member.displayName || member.email}</p>
                      <p className="text-sm text-neutral-600">{member.email}</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  {editingId === member.id ? (
                    <>
                      <select
                        value={editingRole}
                        onChange={(e) => setEditingRole(e.target.value as 'STAFF' | 'AUDITOR')}
                        className="px-3 py-1 rounded border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="STAFF">{t('settings.team.staffRole')}</option>
                        <option value="AUDITOR">{t('settings.team.auditorRole')}</option>
                      </select>
                      <button
                        onClick={() => updateMemberMutation.mutate(member.id)}
                        disabled={updateMemberMutation.isPending}
                        className="px-3 py-1 text-sm rounded bg-green-100 text-green-700 hover:bg-green-200 disabled:opacity-50 transition-colors"
                      >
                        {t('settings.team.save')}
                      </button>
                      <button
                        onClick={() => setEditingId(null)}
                        className="px-3 py-1 text-sm rounded bg-neutral-100 text-neutral-700 hover:bg-neutral-200 transition-colors"
                      >
                        {t('settings.team.cancel')}
                      </button>
                    </>
                  ) : (
                    <>
                      <span
                        className={`px-3 py-1 text-sm font-medium rounded-full ${
                          member.role === 'STAFF'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-purple-100 text-purple-800'
                        }`}
                      >
                        {member.role}
                      </span>
                      <button
                        onClick={() => {
                          setEditingId(member.id);
                          setEditingRole(member.role);
                        }}
                        className="px-3 py-1 text-sm rounded bg-neutral-100 text-neutral-700 hover:bg-neutral-200 transition-colors"
                      >
                        {t('settings.team.edit')}
                      </button>
                      <button
                        onClick={() => {
                          if (confirm(t('settings.team.confirmDelete', { email: member.email }))) {
                            deleteMemberMutation.mutate(member.id);
                          }
                        }}
                        disabled={deleteMemberMutation.isPending}
                        className="px-3 py-1 text-sm rounded bg-red-100 text-red-700 hover:bg-red-200 disabled:opacity-50 transition-colors"
                      >
                        {t('settings.team.delete')}
                      </button>
                      <button
                        onClick={() => {
                          setResetPasswordId(member.id);
                          setResetPasswordValue('');
                        }}
                        className="px-3 py-1 text-sm rounded bg-yellow-100 text-yellow-700 hover:bg-yellow-200 transition-colors"
                      >
                        {t('settings.team.password')}
                      </button>
                    </>
                  )}
                </div>
              </div>
              {resetPasswordId === member.id && (
                <div className="ml-13 p-4 rounded-lg border border-yellow-200 bg-yellow-50">
                  <p className="text-sm font-medium text-neutral-900 mb-3">
                    {t('settings.team.resetPasswordTitle', { name: member.displayName || member.email })}
                  </p>
                  <div className="flex gap-3 items-end">
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-neutral-600 mb-1">{t('settings.team.newPasswordLabel')}</label>
                      <input
                        type="password"
                        value={resetPasswordValue}
                        onChange={(e) => setResetPasswordValue(e.target.value)}
                        placeholder="••••••••"
                        className="w-full px-3 py-2 rounded-lg border border-neutral-200 focus:outline-none focus:ring-2 focus:ring-yellow-500 text-sm"
                        minLength={8}
                      />
                    </div>
                    <button
                      onClick={() => resetPasswordMutation.mutate({ memberId: member.id, newPassword: resetPasswordValue })}
                      disabled={resetPasswordMutation.isPending || resetPasswordValue.length < 8}
                      className="px-4 py-2 text-sm rounded-lg bg-yellow-600 text-white font-medium hover:bg-yellow-700 disabled:opacity-50 transition-colors"
                    >
                      {resetPasswordMutation.isPending ? t('settings.team.resetting') : t('settings.team.reset')}
                    </button>
                    <button
                      onClick={() => { setResetPasswordId(null); setResetPasswordValue(''); }}
                      className="px-4 py-2 text-sm rounded-lg bg-neutral-100 text-neutral-700 hover:bg-neutral-200 transition-colors"
                    >
                      {t('settings.team.cancel')}
                    </button>
                  </div>
                </div>
              )}
              </div>
            ))}
          </div>
        )}

        <div className="mt-4 p-4 bg-neutral-50 rounded-lg border border-neutral-200">
          <p className="text-xs text-neutral-600">
            <strong>{t('settings.team.staffRole')} :</strong> {t('settings.team.staffInfo')}
            <br />
            <strong>{t('settings.team.auditorRole')} :</strong> {t('settings.team.auditorInfo')}
          </p>
        </div>
      </div>
    </div>
  );
}
